import {
  Typography,
  List,
  Table,
  TableBody,
  TableRow,
  TableCell,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import { getPriorities, getImpacts, isProgramSprV2 } from "./Utils/PrioritiesRecsUtilities";
import Box from "@mui/material/Box";
import TableHead from "@mui/material/TableHead";
import { useAssessmentExportContext } from "@features/assessments-feature/contexts-providers/AssessmentExportContextProvider";
import { SkeletonWrapper } from "shared-ui";
import {
  PrioritizedRecommendation,
  ResponseRecommendation,
} from "@lib/ShiOneClient";
import { useAssessmentViewContext } from "@features/assessments-feature/contexts-providers/AssessmentViewContextProvider";
import PrioritiesRecsTableEntry from "./PrioritiesRecsTableEntry";

const PrioritiesRecsTable = ({
  sortedPrioritizedRecs,
  handleRecsDialogOpenState,
  loading,
}: {
  sortedPrioritizedRecs: PrioritizedRecommendation[] | undefined;
  handleRecsDialogOpenState: (
    recsValue: ResponseRecommendation | undefined,
    open: boolean,
  ) => void;
  loading: boolean;
}) => {
  const { isPrint } = useAssessmentExportContext();
  const { selectedSourceIds, handleCheckRecommendation } =
    useAssessmentViewContext();

  const { program } = useAssessmentViewContext();
  const isSprV2 = isProgramSprV2(program);

  const priorities = getPriorities(isSprV2);
  const impacts = getImpacts(isSprV2);

  const selectedSourceIdsSet =
    selectedSourceIds && typeof selectedSourceIds.keys === "function"
      ? new Set(Array.from(selectedSourceIds.keys()))
      : new Set<string>();

  const handleGetCapability = (priority: string, impact: string) => {
    const priorityRes = sortedPrioritizedRecs?.find(
      (data) => data.priority === priority,
    )?.responseRecommendations;
    const impactRes =
      priorityRes?.filter((res) => res.impact === impact) ?? null;
    if (impactRes?.length) {
      return (
        <List dense>
          {impactRes.map((res) => (
            <PrioritiesRecsTableEntry
              key={res.capabilityId}
              res={res}
              isPrint={isPrint}
              selectedSourceIds={selectedSourceIdsSet}
              handleCheckRecommendation={handleCheckRecommendation}
              handleRecsDialogOpenState={handleRecsDialogOpenState}
            />
          ))}
        </List>
      );
    }
    return (
      <Typography p={2} color="textSecondary">
        <SkeletonWrapper loading={loading}>None</SkeletonWrapper>
      </Typography>
    );
  };

  return (
    <Box display="flex" flexDirection="column" alignItems="flex-end">
      <Table>
        <TableHead>
          <TableRow>
            <TableCell> </TableCell>
            {priorities.map((priority) => (
              <TableCell
                key={priority.key}
                sx={{
                  bgcolor: "table.header",
                  border: "1px solid",
                  borderColor: "border",
                  minWidth: "200px",
                  zIndex: 1,
                }}
              >
                <Typography variant="subtitle1" p={1}>
                  {priority.label}
                </Typography>
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {impacts.map((impact) => (
            <TableRow key={impact.key}>
              <TableCell
                component="th"
                scope="row"
                sx={{
                  minWidth: "4.5rem",
                  backgroundColor: impact.color
                    ? alpha(impact.color, 0.15)
                    : undefined,
                  textAlign: "center",
                }}
              >
                <Typography
                  variant="subtitle1"
                  py={2}
                  ml={-4}
                  mr={-4}
                  sx={{
                    transform: "rotate(-90deg)",
                    display: "inline-flex",
                    alignItems: "center",
                    minHeight: "85px",
                  }}
                >
                  {impact.label}
                </Typography>
              </TableCell>
              {priorities.map((priority) => (
                <TableCell
                  key={priority.key}
                  sx={{
                    border: "1px solid",
                    borderColor: "border",
                    bgcolor: "background.paper",
                    verticalAlign: "top",
                  }}
                >
                  <Typography
                    color={"black"}
                    minHeight={isPrint ? 80 : 160}
                    minWidth={320}
                    component={"div"}
                  >
                    {handleGetCapability(priority.value, impact.value)}
                  </Typography>
                </TableCell>
              ))}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Box>
  );
};

export default PrioritiesRecsTable;
